import React, { useState, useRef, useCallback, useEffect } from "react";
import { useDispatch } from "react-redux";
import { Snackbar, Alert } from "@mui/material";
import { agentQuery, searchAdvancedWithPayload, sendCandidateInvites } from "../../Redux/ActionCreators/candidateAction";
import { Avatar, SkillTags } from "./sharedUI";

// Smart search re-implements the search/infinite-scroll flow from
// SmartSearchTab.js with a card-based UI. Selecting profiles and clicking
// "Add candidates" sends invites via POST /jobs/{job_id}/candidate-invites/
// with { global_candidate_ids }, then asks the parent to refetch the list.
const SmartSearchPanel = ({ jobId, invitedEmails, onInvited }) => {
    const dispatch = useDispatch();
    const [sending, setSending] = useState(false);

    const [query, setQuery] = useState("");
    const [hasSearched, setHasSearched] = useState(false);
    const [searching, setSearching] = useState(false);
    const [loadingMore, setLoadingMore] = useState(false);
    const [candidates, setCandidates] = useState([]);
    const [inputPayload, setInputPayload] = useState(null);
    const [total, setTotal] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const [selectedIds, setSelectedIds] = useState(new Set());
    const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "info" });

    const listRef = useRef(null);
    const loadingMoreRef = useRef(false);
    const searchInputRef = useRef(null);

    useEffect(() => {
        const handler = (e) => {
            if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
                e.preventDefault();
                searchInputRef.current?.focus();
            }
        };
        window.addEventListener("keydown", handler);
        return () => window.removeEventListener("keydown", handler);
    }, []);

    const closeSnackbar = () => setSnackbar((s) => ({ ...s, open: false }));

    const handleSearch = async () => {
        const trimmed = query.trim();
        if (!trimmed || searching) return;

        setSearching(true);
        setCandidates([]);
        setInputPayload(null);
        setTotal(0);
        setCurrentPage(1);
        setHasSearched(false);
        setSelectedIds(new Set());

        const result = await dispatch(agentQuery(trimmed));
        setSearching(false);

        if (!result?.success) {
            setSnackbar({ open: true, message: result?.message || "Search failed. Please try again.", severity: "error" });
            return;
        }

        const { message, api_output } = result.data;

        if (message && !api_output) {
            setSnackbar({ open: true, message, severity: "info" });
            return;
        }

        if (api_output) {
            setHasSearched(true);
            setCandidates(api_output.candidates || []);
            setInputPayload(api_output.input_payload);
            setTotal(api_output.total || 0);
            setCurrentPage(api_output.page || 1);
        }
    };

    const handleKeyDown = (e) => {
        if (e.key === "Enter") handleSearch();
    };

    const clearSearch = () => {
        setCandidates([]);
        setInputPayload(null);
        setTotal(0);
        setCurrentPage(1);
        setSelectedIds(new Set());
        setHasSearched(false);
        setQuery("");
    };

    const loadMore = useCallback(async () => {
        if (loadingMoreRef.current || !inputPayload || candidates.length >= total) return;
        loadingMoreRef.current = true;
        setLoadingMore(true);

        const nextPage = currentPage + 1;
        const nextSkip = (nextPage - 1) * (inputPayload.limit || 10);
        // Both are sent since the backend's pagination cursor is keyed off
        // `page`, not `skip` — sending skip alone silently re-fetched page 1.
        const payload = { ...inputPayload, page: nextPage, skip: nextSkip };

        const result = await dispatch(searchAdvancedWithPayload(payload));
        setLoadingMore(false);
        loadingMoreRef.current = false;

        if (result?.success && Array.isArray(result.data) && result.data.length > 0) {
            setCandidates((prev) => [...prev, ...result.data]);
            setCurrentPage(nextPage);
            // Trust the response's own count over the one from the initial
            // search — once it matches what we've loaded, stop scrolling-in-place.
            if (result.pagination?.total != null) setTotal(result.pagination.total);
        } else {
            // No further results — pin total to what's loaded so the
            // `candidates.length >= total` guard stops future scroll triggers.
            setTotal(candidates.length);
        }
    }, [dispatch, inputPayload, candidates.length, total, currentPage]);

    useEffect(() => {
        const container = listRef.current;
        if (!container) return;
        const handleScroll = () => {
            const { scrollTop, scrollHeight, clientHeight } = container;
            if (scrollHeight - scrollTop - clientHeight < 80) loadMore();
        };
        container.addEventListener("scroll", handleScroll);
        return () => container.removeEventListener("scroll", handleScroll);
    }, [loadMore]);

    const isAlreadyInvited = (c) => c.email && invitedEmails.has(c.email.toLowerCase());

    const toggleOne = (id) => {
        setSelectedIds((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    };

    const selectedCandidates = candidates.filter((c) => selectedIds.has(c.id));

    const handleAddSelected = async () => {
        if (!selectedCandidates.length || sending) return;
        const ids = selectedCandidates.map((c) => c.id).filter(Boolean);
        if (!ids.length) {
            setSnackbar({ open: true, message: "Selected profiles are missing an identifier.", severity: "error" });
            return;
        }

        setSending(true);
        const result = await dispatch(sendCandidateInvites(jobId, ids));
        setSending(false);

        if (!result?.success) {
            setSnackbar({ open: true, message: result?.message || "Failed to send invites.", severity: "error" });
            return;
        }

        setSelectedIds(new Set());
        onInvited?.();

        const created = result.invitesCreated || 0;
        const skipped = result.skipped?.length || 0;
        const message = skipped
            ? `${created} invite${created !== 1 ? "s" : ""} sent · ${skipped} skipped (already invited or in job).`
            : `${created} invite${created !== 1 ? "s" : ""} sent.`;
        setSnackbar({ open: true, message, severity: skipped ? "warning" : "success" });
    };

    return (
        <div className="icv2-smart-search">
            <div className="icv2-ss-main">
                <span className="icv2-eyebrow">Search your talent pool</span>
                <h3 className="icv2-ss-title">Who would be a great fit?</h3>
                <p className="icv2-ss-subtitle">
                    Try &ldquo;senior designer in New York&rdquo;, &ldquo;Figma&rdquo;, or a person&rsquo;s name.
                </p>

                <div className="icv2-ss-searchbar">
                    <img src="/images/search.svg" alt="" className="icv2-ss-search-icon" />
                    <input
                        ref={searchInputRef}
                        type="text"
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        onKeyDown={handleKeyDown}
                        placeholder="Search by name, skills, title, location…"
                        disabled={searching}
                    />
                    {hasSearched ? (
                        <button className="icv2-ss-kbd" onClick={clearSearch} title="Clear search">×</button>
                    ) : (
                        <span className="icv2-ss-kbd">⌘K</span>
                    )}
                </div>

                {hasSearched && (
                    <div className="icv2-ss-meta">
                        <span>{total} profile{total !== 1 ? "s" : ""} found</span>
                        {selectedIds.size > 0 && (
                            <button className="icv2-link-btn" onClick={() => setSelectedIds(new Set())}>Clear selection</button>
                        )}
                    </div>
                )}

                <div className="icv2-ss-list" ref={listRef}>
                    {!hasSearched && !searching && (
                        <div className="icv2-empty-block">
                            <img src="/images/ai-search.svg" alt="" style={{ width: "40px", marginBottom: "10px" }} />
                            <p>Search to see matching profiles from your talent pool.</p>
                        </div>
                    )}

                    {searching && (
                        <div className="icv2-loading-block">
                            <div className="spinner-border" role="status" style={{ color: "#F75891" }}>
                                <span className="visually-hidden">Searching…</span>
                            </div>
                        </div>
                    )}

                    {hasSearched && !searching && candidates.length === 0 && (
                        <div className="icv2-empty-block">
                            <p>No candidates found for your query.</p>
                        </div>
                    )}

                    {hasSearched && candidates.map((c) => {
                        const already = isAlreadyInvited(c);
                        const selected = selectedIds.has(c.id);
                        return (
                            <label
                                key={c.id}
                                className={`icv2-ss-card${selected ? " icv2-ss-card--selected" : ""}${already ? " icv2-ss-card--disabled" : ""}`}
                            >
                                <input
                                    type="checkbox"
                                    checked={selected}
                                    disabled={already}
                                    onChange={() => toggleOne(c.id)}
                                />
                                <Avatar name={c.full_name} />
                                <div className="icv2-ss-card-info">
                                    <div className="icv2-ss-card-name-row">
                                        <strong>{c.full_name}</strong>
                                        {already && <span className="icv2-added-pill">Added</span>}
                                    </div>
                                    <p className="icv2-ss-card-sub">
                                        {c.current_designation || "N/A"}
                                        {c.email ? ` · ${c.email}` : ""}
                                    </p>
                                    <SkillTags skills={c.primary_skills} secondarySkills={c.secondary_skills} />
                                </div>
                                {!already && <span className="icv2-match-pill">Good match</span>}
                            </label>
                        );
                    })}

                    {loadingMore && (
                        <div className="icv2-loading-more">
                            <div className="spinner-border spinner-border-sm" role="status" style={{ color: "#F75891" }}>
                                <span className="visually-hidden">Loading more…</span>
                            </div>
                            <span>Loading more candidates…</span>
                        </div>
                    )}
                </div>
            </div>

            <div className="icv2-ss-selected-panel">
                <div className="icv2-ss-selected-head">
                    <span>Selected</span>
                    <span className="icv2-count-badge">{selectedIds.size}</span>
                </div>
                {selectedIds.size === 0 ? (
                    <div className="icv2-ss-selected-empty">
                        <div className="icv2-ss-selected-empty-icon">
                            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
                                <circle cx="9" cy="7" r="4" />
                                <line x1="19" y1="8" x2="19" y2="14" />
                                <line x1="22" y1="11" x2="16" y2="11" />
                            </svg>
                        </div>
                        <p>Select one or more profiles to add them to the invite list.</p>
                    </div>
                ) : (
                    <>
                        <p className="icv2-ss-selected-hint">
                            These candidates will receive an invite and appear below with an Invite sent status.
                        </p>
                        <button className="icv2-primary-btn" onClick={handleAddSelected} disabled={sending}>
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <rect x="2" y="4" width="20" height="16" rx="2" />
                                <path d="m22 6-10 7L2 6" />
                            </svg>
                            {sending ? "Sending…" : `Add ${selectedIds.size} candidate${selectedIds.size > 1 ? "s" : ""}`}
                        </button>
                    </>
                )}
            </div>

            <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={closeSnackbar} anchorOrigin={{ vertical: "bottom", horizontal: "center" }}>
                <Alert onClose={closeSnackbar} severity={snackbar.severity} sx={{ width: "100%" }}>{snackbar.message}</Alert>
            </Snackbar>
        </div>
    );
};

export default SmartSearchPanel;
